import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { 
  Search, Globe, Share2, CheckCircle, AlertCircle, Eye, 
  FileText, Tag, Link as LinkIcon, Image as ImageIcon, 
  Settings, RefreshCw, Loader2, Save, ExternalLink, Copy,
  Check, X, ChevronDown, ChevronUp, Sparkles, TrendingUp,
  Layout, Code, Map, Bot, BarChart3, Smartphone, Monitor, Upload, Trash2,
  Film, Camera
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SEOAdmin = ({ platform = 'vod', authToken }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [seoSettings, setSeoSettings] = useState(null);
  const [auditResults, setAuditResults] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  const [hiddenSitemaps, setHiddenSitemaps] = useState([]);
  
  // Page selector for VOD platform (main VOD page vs Creator page)
  const [selectedPage, setSelectedPage] = useState('vod');
  
  // Creator page specific settings
  const [creatorSettings, setCreatorSettings] = useState(null);
  const [loadingCreator, setLoadingCreator] = useState(false);
  const [savingCreator, setSavingCreator] = useState(false);
  const [uploadingCreatorImage, setUploadingCreatorImage] = useState(false);
  
  // Expandable sections
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    social: true,
    technical: true,
    analytics: false,
    schema: false,
    sitemaps: true,
    audit: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const headers = {
    Authorization: `Bearer ${authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken')}`
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/seo/${platform}/settings`, { headers });
      setSeoSettings(res.data);
    } catch (err) {
      console.error('Error fetching SEO settings:', err);
    } finally {
      setLoading(false);
    }
  }, [platform]);

  const fetchCreatorSettings = useCallback(async () => {
    setLoadingCreator(true);
    try {
      const res = await axios.get(`${API}/api/seo/creator/settings`, { headers });
      setCreatorSettings(res.data);
    } catch (err) {
      console.error('Error fetching Creator SEO settings:', err);
    } finally {
      setLoadingCreator(false);
    }
  }, []);

  const fetchAudit = useCallback(async () => {
    if (platform !== 'vod') return;
    setLoadingAudit(true);
    try {
      const res = await axios.get(`${API}/api/seo/vod/audit`, { headers });
      setAuditResults(res.data);
    } catch (err) {
      console.error('Error fetching audit:', err);
    } finally {
      setLoadingAudit(false);
    }
  }, [platform]);

  useEffect(() => {
    fetchSettings();
    if (platform === 'vod') {
      fetchAudit();
      fetchCreatorSettings();
    }
  }, [fetchSettings, fetchAudit, fetchCreatorSettings, platform]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/seo/${platform}/settings`, seoSettings, { headers });
      alert('SEO settings saved successfully!');
    } catch (err) {
      console.error('Error saving SEO settings:', err);
      alert('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCreator = async () => {
    setSavingCreator(true);
    try {
      await axios.post(`${API}/api/seo/creator/settings`, creatorSettings, { headers });
      alert('Creator page SEO settings saved successfully!');
    } catch (err) {
      console.error('Error saving Creator SEO settings:', err);
      alert('Failed to save Creator page SEO settings');
    } finally {
      setSavingCreator(false);
    }
  };

  const updateField = (field, value) => {
    setSeoSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateCreatorField = (field, value) => {
    setCreatorSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent, field, value) => {
    setSeoSettings(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(label);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleOgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingOgImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'og-images');
      formData.append('reference_name', 'site-og-image');
      
      const res = await axios.post(`${API}/api/r2/upload-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      
      updateField('og_image_url', res.data.url);
    } catch (err) {
      console.error('OG image upload error:', err);
      alert('Failed to upload image to R2. Check your R2 settings.');
    } finally {
      setUploadingOgImage(false);
    }
  };

  const removeOgImage = () => {
    updateField('og_image_url', '');
  };

  const handleCreatorImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCreatorImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'og-images');
      formData.append('reference_name', 'creator-og-image');
      
      const res = await axios.post(`${API}/api/r2/upload-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      
      updateCreatorField('og_image_url', res.data.url);
    } catch (err) {
      console.error('Creator OG image upload error:', err);
      alert('Failed to upload image to R2. Check your R2 settings.');
    } finally {
      setUploadingCreatorImage(false);
    }
  };

  const removeCreatorImage = () => {
    updateCreatorField('og_image_url', '');
  };

  const baseUrl = 'https://ltvproduction.com';
  const sitemapUrls = platform === 'vod' ? [
    { label: 'Main Sitemap', url: `${API}/api/seo/vod/sitemap.xml`, description: 'All VOD pages and categories' },
    { label: 'Video Sitemap', url: `${API}/api/seo/vod/video-sitemap.xml`, description: 'Google Video Search sitemap' },
    { label: 'Robots.txt', url: `${API}/api/seo/robots.txt`, description: 'Search engine crawl directives' },
    { label: 'Schema JSON-LD', url: `${API}/api/seo/vod/schema.json`, description: 'Structured data for AI search' },
  ] : [
    { label: 'Live Sitemap', url: `${API}/api/seo/live/sitemap.xml`, description: 'All Live events' },
    { label: 'Robots.txt', url: `${API}/api/seo/robots.txt`, description: 'Search engine crawl directives' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Search className="h-6 w-6 text-purple-400" />
            SEO Management - LTV {platform === 'vod' ? 'VOD' : 'Live'}
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Optimize your site for Google, Bing, and AI search engines
          </p>
        </div>
        {selectedPage === 'vod' ? (
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save All Settings
          </Button>
        ) : (
          <Button 
            onClick={handleSaveCreator} 
            disabled={savingCreator}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black"
          >
            {savingCreator ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Creator Settings
          </Button>
        )}
      </div>

      {/* Page Selector Tabs - Only for VOD Platform */}
      {platform === 'vod' && (
        <div className="flex gap-2 border-b border-gray-800 pb-4">
          <button
            onClick={() => setSelectedPage('vod')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedPage === 'vod'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <Film className="h-4 w-4 inline mr-2" />
            VOD Homepage
          </button>
          <button
            onClick={() => setSelectedPage('creator')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedPage === 'creator'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <Camera className="h-4 w-4 inline mr-2" />
            Creator Package Page
          </button>
        </div>
      )}

      {/* Creator Page SEO Settings */}
      {platform === 'vod' && selectedPage === 'creator' && (
        <CreatorPageSEO 
          settings={creatorSettings}
          loading={loadingCreator}
          updateField={updateCreatorField}
          handleImageUpload={handleCreatorImageUpload}
          uploadingImage={uploadingCreatorImage}
          removeImage={removeCreatorImage}
        />
      )}

      {/* Main VOD/Live SEO Settings - Show only when VOD page is selected or for Live platform */}
      {(platform !== 'vod' || selectedPage === 'vod') && (
        <>
      {/* SEO Score Card - For VOD Only */}
      {platform === 'vod' && auditResults && (
        <div className={`rounded-xl p-6 border ${
          auditResults.score >= 80 ? 'bg-green-900/20 border-green-500/30' :
          auditResults.score >= 50 ? 'bg-yellow-900/20 border-yellow-500/30' :
          'bg-red-900/20 border-red-500/30'
        }`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`text-5xl font-bold ${
                auditResults.score >= 80 ? 'text-green-400' :
                auditResults.score >= 50 ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {auditResults.score}
              </div>
              <div>
                <h3 className="font-bold text-lg">SEO Health Score</h3>
                <p className="text-sm text-gray-400">
                  {auditResults.total_videos} videos indexed
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchAudit}
              disabled={loadingAudit}
              className="border-gray-700"
            >
              {loadingAudit ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh Audit
            </Button>
          </div>
          
          {/* Issues Summary */}
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div className="bg-red-900/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-400 mb-2">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Issues ({auditResults.issues.length})</span>
              </div>
              <ul className="text-xs space-y-1 text-gray-300">
                {auditResults.issues.slice(0, 3).map((issue, i) => (
                  <li key={i}>• {issue}</li>
                ))}
              </ul>
            </div>
            <div className="bg-yellow-900/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-yellow-400 mb-2">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Warnings ({auditResults.warnings.length})</span>
              </div>
              <ul className="text-xs space-y-1 text-gray-300">
                {auditResults.warnings.slice(0, 3).map((warning, i) => (
                  <li key={i}>• {warning}</li>
                ))}
              </ul>
            </div>
            <div className="bg-green-900/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-400 mb-2">
                <CheckCircle className="h-4 w-4" />
                <span className="font-medium">Passed ({auditResults.good.length})</span>
              </div>
              <ul className="text-xs space-y-1 text-gray-300">
                {auditResults.good.slice(0, 3).map((good, i) => (
                  <li key={i}>• {good}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Basic SEO Section */}
      <CollapsibleSection 
        title="Basic SEO" 
        icon={<FileText className="h-5 w-5 text-blue-400" />}
        isOpen={expandedSections.basic}
        onToggle={() => toggleSection('basic')}
        badge={seoSettings?.site_title ? 'Configured' : 'Required'}
        badgeColor={seoSettings?.site_title ? 'green' : 'yellow'}
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <FormField
              label="Site Title"
              value={seoSettings?.site_title || ''}
              onChange={(v) => updateField('site_title', v)}
              placeholder={`LTV ${platform === 'vod' ? 'VOD' : 'Live'} - Premium Video Streaming`}
              helpText="Appears in browser tab and search results (50-60 characters ideal)"
              maxLength={60}
              showCount
            />
            <FormField
              label="Meta Description"
              value={seoSettings?.site_description || ''}
              onChange={(v) => updateField('site_description', v)}
              placeholder="Watch exclusive videos, concerts, and performances..."
              helpText="Shown below title in search results (120-160 characters ideal)"
              maxLength={160}
              showCount
              isTextarea
            />
            <FormField
              label="Keywords"
              value={seoSettings?.site_keywords || ''}
              onChange={(v) => updateField('site_keywords', v)}
              placeholder="video on demand, streaming, concerts, live performances, LTV"
              helpText="Comma-separated keywords for search engines"
            />
          </div>
          <div className="space-y-4">
            <FormField
              label="Canonical URL"
              value={seoSettings?.canonical_url || ''}
              onChange={(v) => updateField('canonical_url', v)}
              placeholder={`${baseUrl}/${platform}`}
              helpText="Main URL for this page (prevents duplicate content issues)"
              icon={<LinkIcon className="h-4 w-4" />}
            />
            <FormField
              label="Robots Meta"
              value={seoSettings?.robots_meta || 'index, follow'}
              onChange={(v) => updateField('robots_meta', v)}
              helpText="index, follow (recommended) | noindex, nofollow (hide from search)"
              icon={<Bot className="h-4 w-4" />}
            />
            
            {/* Preview */}
            <div className="mt-4">
              <label className="text-sm text-gray-400 mb-2 block">Google Search Preview</label>
              <div className="bg-white rounded-lg p-4 text-black">
                <div className="text-blue-800 text-lg hover:underline cursor-pointer line-clamp-1">
                  {seoSettings?.site_title || `LTV ${platform === 'vod' ? 'VOD' : 'Live'}`}
                </div>
                <div className="text-green-700 text-sm">
                  {baseUrl}/{platform}
                </div>
                <div className="text-gray-600 text-sm line-clamp-2 mt-1">
                  {seoSettings?.site_description || 'Add a meta description to appear here...'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Social Media / Open Graph Section */}
      <CollapsibleSection 
        title="Social Media Optimization" 
        icon={<Share2 className="h-5 w-5 text-pink-400" />}
        isOpen={expandedSections.social}
        onToggle={() => toggleSection('social')}
        badge="Open Graph & Twitter"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-gray-300 flex items-center gap-2">
              <Globe className="h-4 w-4" /> Open Graph (Facebook, LinkedIn, WhatsApp)
            </h4>
            <FormField
              label="OG Title"
              value={seoSettings?.og_title || ''}
              onChange={(v) => updateField('og_title', v)}
              placeholder={seoSettings?.site_title || 'Share title'}
              helpText="Title shown when shared on social media"
            />
            <FormField
              label="OG Description"
              value={seoSettings?.og_description || ''}
              onChange={(v) => updateField('og_description', v)}
              placeholder={seoSettings?.site_description || 'Share description'}
              helpText="Description shown when shared"
              isTextarea
            />
            
            {/* OG Image Upload */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Social Share Image
              </label>
              
              {seoSettings?.og_image_url ? (
                <div className="space-y-3">
                  <div className="relative inline-block">
                    <img 
                      src={seoSettings.og_image_url} 
                      alt="Social Share Preview" 
                      className="w-full max-w-sm rounded-lg border border-gray-700"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <button
                      type="button"
                      onClick={removeOgImage}
                      className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-500 rounded-full text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-green-400">Image uploaded successfully</p>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleOgImageUpload}
                    className="hidden"
                    id="og-image-upload"
                  />
                  <label 
                    htmlFor="og-image-upload"
                    className="flex items-center gap-3 px-4 py-4 bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-gray-800/80 transition-all"
                  >
                    {uploadingOgImage ? (
                      <>
                        <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                        <span className="text-gray-400">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-purple-400" />
                        <div>
                          <span className="text-white font-medium">Upload Social Image</span>
                          <p className="text-xs text-gray-500">1200x630px recommended for best display</p>
                        </div>
                      </>
                    )}
                  </label>
                </>
              )}
              <p className="text-xs text-gray-500 mt-2">This image appears when your page is shared on Facebook, WhatsApp, LinkedIn, etc.</p>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-medium text-gray-300 flex items-center gap-2">
              <span className="text-blue-400">𝕏</span> Twitter Card
            </h4>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Card Type</label>
              <select
                value={seoSettings?.twitter_card || 'summary_large_image'}
                onChange={(e) => updateField('twitter_card', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              >
                <option value="summary">Summary (small image)</option>
                <option value="summary_large_image">Summary Large Image (recommended)</option>
                <option value="player">Player (video)</option>
              </select>
            </div>
            <FormField
              label="Twitter @username"
              value={seoSettings?.twitter_site || ''}
              onChange={(v) => updateField('twitter_site', v)}
              placeholder="@ltvproduction"
              helpText="Your Twitter/X handle"
            />
            <FormField
              label="Content Creator @username"
              value={seoSettings?.twitter_creator || ''}
              onChange={(v) => updateField('twitter_creator', v)}
              placeholder="@ltvproduction"
              helpText="Creator's Twitter/X handle"
            />
            
            {/* Social Preview */}
            <div className="mt-4">
              <label className="text-sm text-gray-400 mb-2 block">Social Share Preview</label>
              <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
                {seoSettings?.og_image_url ? (
                  <img 
                    src={seoSettings.og_image_url} 
                    alt="Share Preview" 
                    className="w-full h-32 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-700 flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-gray-500" />
                  </div>
                )}
                <div className="p-3">
                  <p className="font-medium line-clamp-1">{seoSettings?.og_title || seoSettings?.site_title || 'Title'}</p>
                  <p className="text-sm text-gray-400 line-clamp-2">{seoSettings?.og_description || seoSettings?.site_description || 'Description'}</p>
                  <p className="text-xs text-gray-500 mt-1">{baseUrl}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Search Engine Verification */}
      <CollapsibleSection 
        title="Search Engine Verification" 
        icon={<CheckCircle className="h-5 w-5 text-green-400" />}
        isOpen={expandedSections.technical}
        onToggle={() => toggleSection('technical')}
        badge="Google & Bing"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/20 rounded-lg p-4 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-3">
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                <span className="font-medium">Google Search Console</span>
              </div>
              <FormField
                label="Verification Code"
                value={seoSettings?.google_site_verification || ''}
                onChange={(v) => updateField('google_site_verification', v)}
                placeholder="google-site-verification=..."
                helpText="Get code from Google Search Console → Settings → Ownership verification"
              />
              <a 
                href="https://search.google.com/search-console" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 text-sm hover:underline flex items-center gap-1 mt-2"
              >
                Open Google Search Console <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-cyan-900/30 to-cyan-800/20 rounded-lg p-4 border border-cyan-500/30">
              <div className="flex items-center gap-2 mb-3">
                <img src="https://www.bing.com/favicon.ico" alt="Bing" className="w-5 h-5" />
                <span className="font-medium">Bing Webmaster Tools</span>
              </div>
              <FormField
                label="Verification Code"
                value={seoSettings?.bing_site_verification || ''}
                onChange={(v) => updateField('bing_site_verification', v)}
                placeholder="bing-site-verification=..."
                helpText="Get code from Bing Webmaster Tools → Configure My Site"
              />
              <a 
                href="https://www.bing.com/webmasters" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cyan-400 text-sm hover:underline flex items-center gap-1 mt-2"
              >
                Open Bing Webmaster Tools <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-yellow-400" />
            How to Verify Your Site
          </h4>
          <ol className="text-sm text-gray-400 space-y-2">
            <li>1. Go to Google Search Console or Bing Webmaster Tools</li>
            <li>2. Add your site: <code className="bg-gray-700 px-1 rounded">{baseUrl}</code></li>
            <li>3. Choose "HTML tag" verification method</li>
            <li>4. Copy the verification code and paste it above</li>
            <li>5. Click "Save All Settings" then verify in the search console</li>
          </ol>
        </div>
      </CollapsibleSection>

      {/* Analytics & Tracking */}
      <CollapsibleSection 
        title="Analytics & Tracking" 
        icon={<BarChart3 className="h-5 w-5 text-orange-400" />}
        isOpen={expandedSections.analytics}
        onToggle={() => toggleSection('analytics')}
        badge="Optional"
      >
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-orange-900/20 rounded-lg p-4 border border-orange-500/30">
            <h4 className="font-medium mb-3">Google Analytics</h4>
            <FormField
              label="GA4 Measurement ID"
              value={seoSettings?.google_analytics_id || ''}
              onChange={(v) => updateField('google_analytics_id', v)}
              placeholder="G-XXXXXXXXXX"
              helpText="Google Analytics 4 ID"
            />
          </div>
          <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-500/30">
            <h4 className="font-medium mb-3">Google Tag Manager</h4>
            <FormField
              label="GTM Container ID"
              value={seoSettings?.google_tag_manager_id || ''}
              onChange={(v) => updateField('google_tag_manager_id', v)}
              placeholder="GTM-XXXXXXX"
              helpText="Google Tag Manager ID"
            />
          </div>
          <div className="bg-indigo-900/20 rounded-lg p-4 border border-indigo-500/30">
            <h4 className="font-medium mb-3">Facebook Pixel</h4>
            <FormField
              label="Pixel ID"
              value={seoSettings?.facebook_pixel_id || ''}
              onChange={(v) => updateField('facebook_pixel_id', v)}
              placeholder="123456789012345"
              helpText="Meta/Facebook Pixel ID"
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Schema.org / Structured Data */}
      <CollapsibleSection 
        title="Schema.org Structured Data" 
        icon={<Code className="h-5 w-5 text-purple-400" />}
        isOpen={expandedSections.schema}
        onToggle={() => toggleSection('schema')}
        badge="For AI & Rich Snippets"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Structured data helps search engines and AI understand your content better, 
              enabling rich snippets and better visibility in AI-powered search results.
            </p>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Schema Type</label>
              <select
                value={seoSettings?.schema_type || 'VideoGallery'}
                onChange={(e) => updateField('schema_type', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              >
                <option value="VideoGallery">VideoGallery (for VOD platforms)</option>
                <option value="Organization">Organization</option>
                <option value="WebSite">WebSite</option>
                <option value="MediaObject">MediaObject</option>
              </select>
            </div>
            <FormField
              label="Organization/Site Name"
              value={seoSettings?.schema_name || ''}
              onChange={(v) => updateField('schema_name', v)}
              placeholder={`LTV ${platform === 'vod' ? 'VOD' : 'Live'}`}
            />
            <FormField
              label="Logo URL"
              value={seoSettings?.schema_logo_url || ''}
              onChange={(v) => updateField('schema_logo_url', v)}
              placeholder={`${baseUrl}/ltv-icon.png`}
              icon={<ImageIcon className="h-4 w-4" />}
            />
          </div>
          <div className="space-y-4">
            <h4 className="font-medium text-gray-300">Social Media Profiles (sameAs)</h4>
            <p className="text-xs text-gray-500">Add your official social media URLs (one per line)</p>
            <textarea
              value={(seoSettings?.schema_same_as || []).join('\n')}
              onChange={(e) => updateField('schema_same_as', e.target.value.split('\n').filter(url => url.trim()))}
              placeholder={`https://facebook.com/ltvproduction\nhttps://instagram.com/ltvproduction\nhttps://twitter.com/ltvproduction\nhttps://youtube.com/@ltvproduction`}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white h-32 text-sm"
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Video SEO Settings - VOD Only */}
      {platform === 'vod' && (
        <CollapsibleSection 
          title="Video SEO Settings" 
          icon={<Map className="h-5 w-5 text-red-400" />}
          isOpen={true}
          onToggle={() => {}}
          badge="Critical for Google Video"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="font-medium">Auto-generate Video Sitemaps</p>
                  <p className="text-xs text-gray-400">Automatically index new videos</p>
                </div>
                <ToggleSwitch
                  checked={seoSettings?.video_seo?.auto_generate_sitemaps ?? true}
                  onChange={(v) => updateNestedField('video_seo', 'auto_generate_sitemaps', v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="font-medium">Include Thumbnails</p>
                  <p className="text-xs text-gray-400">Show video thumbnails in search</p>
                </div>
                <ToggleSwitch
                  checked={seoSettings?.video_seo?.include_thumbnails ?? true}
                  onChange={(v) => updateNestedField('video_seo', 'include_thumbnails', v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="font-medium">Include Duration</p>
                  <p className="text-xs text-gray-400">Show video length in search</p>
                </div>
                <ToggleSwitch
                  checked={seoSettings?.video_seo?.include_duration ?? true}
                  onChange={(v) => updateNestedField('video_seo', 'include_duration', v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="font-medium">Include Descriptions</p>
                  <p className="text-xs text-gray-400">Include video descriptions in sitemap</p>
                </div>
                <ToggleSwitch
                  checked={seoSettings?.video_seo?.include_description ?? true}
                  onChange={(v) => updateNestedField('video_seo', 'include_description', v)}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Sitemap Change Frequency</label>
                <select
                  value={seoSettings?.video_seo?.sitemap_change_frequency || 'weekly'}
                  onChange={(e) => updateNestedField('video_seo', 'sitemap_change_frequency', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (recommended)</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Default Priority (0.0 - 1.0)</label>
                <Input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  value={seoSettings?.video_seo?.sitemap_priority || 0.8}
                  onChange={(e) => updateNestedField('video_seo', 'sitemap_priority', parseFloat(e.target.value))}
                  className="bg-gray-800 border-gray-700"
                />
                <p className="text-xs text-gray-500 mt-1">Higher = more important (0.7-0.9 recommended for videos)</p>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Sitemaps & URLs */}
      <CollapsibleSection 
        title="Sitemaps & Search Engine URLs" 
        icon={<Map className="h-5 w-5 text-green-400" />}
        isOpen={expandedSections.sitemaps}
        onToggle={() => toggleSection('sitemaps')}
        badge="Submit to Search Engines"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Submit these URLs to Google Search Console and Bing Webmaster Tools to ensure all your content is indexed.
          </p>
          
          <div className="grid gap-3">
            {sitemapUrls.filter(item => !hiddenSitemaps.includes(item.label)).map((item) => (
              <div 
                key={item.label}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-4 bg-gray-800 rounded-lg border border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.description}</p>
                  <code className="text-xs text-green-400 break-all">{item.url}</code>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(item.url, item.label)}
                    className="border-gray-700"
                  >
                    {copiedUrl === item.label ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-gray-700">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHiddenSitemaps([...hiddenSitemaps, item.label])}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    title="Hide this URL"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Show hidden sitemaps with restore option */}
          {hiddenSitemaps.length > 0 && (
            <div className="mt-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <p className="text-sm text-gray-400 mb-2">Hidden URLs ({hiddenSitemaps.length}):</p>
              <div className="flex flex-wrap gap-2">
                {hiddenSitemaps.map(label => (
                  <button
                    key={label}
                    onClick={() => setHiddenSitemaps(hiddenSitemaps.filter(l => l !== label))}
                    className="flex items-center gap-1 px-2 py-1 bg-gray-700 rounded text-sm text-gray-300 hover:bg-gray-600"
                  >
                    {label}
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* IndexNow Section */}
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-lg border border-purple-500/30">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-400" />
              Quick Index with IndexNow
            </h4>
            <p className="text-sm text-gray-400 mb-3">
              IndexNow allows you to instantly notify search engines (Bing, Yandex, etc.) about new or updated content.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="Enter URL to submit..."
                className="flex-1 bg-gray-800 border-gray-700"
                id="indexnow-url"
              />
              <Button 
                variant="outline" 
                className="border-purple-500/50 text-purple-400"
                onClick={async () => {
                  const url = document.getElementById('indexnow-url').value;
                  if (!url) return alert('Please enter a URL');
                  try {
                    const res = await axios.post(`${API}/api/seo/index-now`, [url], { headers });
                    const data = res.data;
                    if (data.success) {
                      alert(`✅ ${data.message}\n\nSubmitted to: ${data.submitted_to?.join(', ') || 'IndexNow'}\n\n⚠️ Note: ${data.note}`);
                    } else {
                      alert(`⚠️ ${data.message}\n\n${data.setup_instructions || ''}`);
                    }
                  } catch (err) {
                    alert('Failed to submit URL. Please try again.');
                  }
                }}
              >
                Submit to Bing/Yandex
              </Button>
            </div>
            <p className="text-xs text-yellow-400 mt-2">
              ⚠️ IndexNow works with Bing, Yandex, Seznam only. For Google, submit your sitemaps directly in Google Search Console.
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* SEO Recommendations */}
      {platform === 'vod' && auditResults?.recommendations && (
        <CollapsibleSection 
          title="SEO Recommendations" 
          icon={<Sparkles className="h-5 w-5 text-yellow-400" />}
          isOpen={expandedSections.audit}
          onToggle={() => toggleSection('audit')}
          badge={`${auditResults.recommendations.length} tips`}
        >
          <ul className="space-y-2">
            {auditResults.recommendations.map((rec, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 shrink-0" />
                {rec}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}

      {/* Save Button Fixed at Bottom */}
      <div className="sticky bottom-4 flex justify-end">
        <Button 
          onClick={selectedPage === 'creator' ? handleSaveCreator : handleSave} 
          disabled={selectedPage === 'creator' ? savingCreator : saving}
          size="lg"
          className={selectedPage === 'creator' 
            ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black shadow-lg shadow-orange-500/30"
            : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 shadow-lg shadow-purple-500/30"
          }
        >
          {(selectedPage === 'creator' ? savingCreator : saving) ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Save className="h-5 w-5 mr-2" />}
          {selectedPage === 'creator' ? 'Save Creator SEO Settings' : 'Save All SEO Settings'}
        </Button>
      </div>
      </>
      )}
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon, isOpen, onToggle, badge, badgeColor = 'gray', children }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 hover:bg-gray-800/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        {icon}
        <span className="font-bold">{title}</span>
        {badge && (
          <Badge className={`text-xs ${
            badgeColor === 'green' ? 'bg-green-600' :
            badgeColor === 'yellow' ? 'bg-yellow-600' :
            badgeColor === 'red' ? 'bg-red-600' :
            'bg-gray-600'
          }`}>
            {badge}
          </Badge>
        )}
      </div>
      {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
    </button>
    {isOpen && <div className="p-4 pt-0 border-t border-gray-800">{children}</div>}
  </div>
);

// Form Field Component
const FormField = ({ label, value, onChange, placeholder, helpText, maxLength, showCount, isTextarea, icon }) => (
  <div>
    <label className="text-sm text-gray-400 mb-1 flex items-center justify-between">
      <span className="flex items-center gap-2">
        {icon}
        {label}
      </span>
      {showCount && maxLength && (
        <span className={`text-xs ${value?.length > maxLength ? 'text-red-400' : 'text-gray-500'}`}>
          {value?.length || 0}/{maxLength}
        </span>
      )}
    </label>
    {isTextarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm h-24 resize-none"
      />
    ) : (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="bg-gray-800 border-gray-700"
      />
    )}
    {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
  </div>
);

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative w-12 h-6 rounded-full transition-colors ${
      checked ? 'bg-green-500' : 'bg-gray-700'
    }`}
  >
    <span
      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
        checked ? 'translate-x-6' : ''
      }`}
    />
  </button>
);

// Creator Page SEO Component
const CreatorPageSEO = ({ settings, loading, updateField, handleImageUpload, uploadingImage, removeImage }) => {
  const baseUrl = 'https://ltvproduction.com';

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Info Banner */}
      <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-xl p-4 border border-amber-500/30">
        <div className="flex items-center gap-3">
          <Camera className="h-6 w-6 text-amber-400" />
          <div>
            <h3 className="font-bold text-amber-400">Creator Package Page SEO</h3>
            <p className="text-sm text-gray-400">Configure SEO settings for /creator page</p>
          </div>
        </div>
      </div>

      {/* Basic SEO */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-400" />
          Basic SEO Settings
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 flex items-center justify-between">
                <span>Site Title</span>
                <span className={`text-xs ${settings.site_title?.length > 60 ? 'text-red-400' : 'text-gray-500'}`}>
                  {settings.site_title?.length || 0}/60
                </span>
              </label>
              <Input
                value={settings.site_title || ''}
                onChange={(e) => updateField('site_title', e.target.value)}
                placeholder="Creator Package | LTV Production - Professional Video Production"
                maxLength={60}
                className="bg-gray-800 border-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">Appears in browser tab and search results (50-60 characters ideal)</p>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 flex items-center justify-between">
                <span>Meta Description</span>
                <span className={`text-xs ${settings.site_description?.length > 160 ? 'text-red-400' : 'text-gray-500'}`}>
                  {settings.site_description?.length || 0}/160
                </span>
              </label>
              <textarea
                value={settings.site_description || ''}
                onChange={(e) => updateField('site_description', e.target.value)}
                placeholder="Book your professional video production package. Studio sessions, live streaming, and content creation services."
                maxLength={160}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm h-24 resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">Shown below title in search results (120-160 characters ideal)</p>
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">Keywords</label>
              <Input
                value={settings.site_keywords || ''}
                onChange={(e) => updateField('site_keywords', e.target.value)}
                placeholder="video production, creator package, studio hire, live streaming, content creation"
                className="bg-gray-800 border-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated keywords for search engines</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Canonical URL</label>
              <Input
                value={settings.canonical_url || ''}
                onChange={(e) => updateField('canonical_url', e.target.value)}
                placeholder="https://ltvproduction.com/creator"
                className="bg-gray-800 border-gray-700"
              />
            </div>

            {/* Google Search Preview */}
            <div className="mt-4">
              <label className="text-sm text-gray-400 mb-2 block">Google Search Preview</label>
              <div className="bg-white rounded-lg p-4 text-black">
                <div className="text-blue-800 text-lg hover:underline cursor-pointer line-clamp-1">
                  {settings.site_title || 'Creator Package | LTV Production'}
                </div>
                <div className="text-green-700 text-sm">
                  {baseUrl}/creator
                </div>
                <div className="text-gray-600 text-sm line-clamp-2 mt-1">
                  {settings.site_description || 'Add a meta description to appear here...'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Open Graph / Social Media */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <Share2 className="h-5 w-5 text-pink-400" />
          Social Media / Open Graph
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">OG Title</label>
              <Input
                value={settings.og_title || ''}
                onChange={(e) => updateField('og_title', e.target.value)}
                placeholder="Creator Package | Professional Video Production"
                className="bg-gray-800 border-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">Title shown when shared on social media</p>
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">OG Description</label>
              <textarea
                value={settings.og_description || ''}
                onChange={(e) => updateField('og_description', e.target.value)}
                placeholder="Book your professional video production package. Studio sessions, live streaming, and content creation services."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm h-24 resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">Description shown when shared</p>
            </div>

            {/* Social Share Image Upload */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Social Share Image
              </label>
              
              {settings.og_image_url ? (
                <div className="space-y-3">
                  <div className="relative inline-block">
                    <img 
                      src={settings.og_image_url} 
                      alt="Social Share Preview" 
                      className="w-full max-w-sm rounded-lg border border-gray-700"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-500 rounded-full text-white"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-green-400">Image uploaded successfully</p>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="creator-og-image-upload"
                  />
                  <label 
                    htmlFor="creator-og-image-upload"
                    className="flex items-center gap-3 px-4 py-4 bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-orange-500 hover:bg-gray-800/80 transition-all"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="h-6 w-6 animate-spin text-orange-400" />
                        <span className="text-gray-400">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-orange-400" />
                        <div>
                          <span className="text-white font-medium">Upload Social Image</span>
                          <p className="text-xs text-gray-500">1200x630px recommended for best display</p>
                        </div>
                      </>
                    )}
                  </label>
                </>
              )}
              <p className="text-xs text-gray-500 mt-2">This image appears when your Creator page is shared on Facebook, WhatsApp, LinkedIn, etc.</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Twitter Card Settings */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Twitter Title</label>
              <Input
                value={settings.twitter_title || ''}
                onChange={(e) => updateField('twitter_title', e.target.value)}
                placeholder="Creator Package | LTV Production"
                className="bg-gray-800 border-gray-700"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1 block">Twitter Description</label>
              <textarea
                value={settings.twitter_description || ''}
                onChange={(e) => updateField('twitter_description', e.target.value)}
                placeholder="Professional video production packages for creators, artists, and businesses."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm h-24 resize-none"
              />
            </div>

            {/* Social Share Preview */}
            <div className="mt-4">
              <label className="text-sm text-gray-400 mb-2 block">Social Share Preview</label>
              <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
                {settings.og_image_url ? (
                  <img 
                    src={settings.og_image_url} 
                    alt="Share Preview" 
                    className="w-full h-32 object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-700 flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-gray-500" />
                  </div>
                )}
                <div className="p-3">
                  <p className="font-medium line-clamp-1">{settings.og_title || settings.site_title || 'Creator Package'}</p>
                  <p className="text-sm text-gray-400 line-clamp-2">{settings.og_description || settings.site_description || 'Description'}</p>
                  <p className="text-xs text-gray-500 mt-1">{baseUrl}/creator</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOAdmin;
